import Promise from 'bluebird';
import { useCallback, useEffect } from 'react';
import _ from 'underscore';

import { Constants } from '@bingads-webui-clientcenter/entity-utils';

import { useAction } from './hooks/use-data-actions';
import { useCache } from './context/cache-context';

import { getCCJsDataObj } from './ccui-util';
import type {
  CCDataServerContext,
  CCJsDataRepositories,
  EntityId,
  PaymentAddress,
  PaymentInstrument,
} from './types';

class PaymentInstrumentActions {
  cid: EntityId;
  jsDataCcRest: CCJsDataRepositories;

  constructor({
    CurrentCustomer,
    CurrentUser
  }: CCDataServerContext) {
    this.cid = CurrentCustomer && CurrentCustomer.Id;
    const uid = CurrentUser && CurrentUser.Id;

    this.jsDataCcRest = getCCJsDataObj(this.cid, uid);
  }

  fetchAll = (bypassCache = false) => Promise.resolve(this.jsDataCcRest.PaymentInstrument.findAll(
    { customerId: this.cid },
    bypassCache
      ? { bypassCache: true }
      : undefined
  ));

  fetch = (piid: EntityId) =>
    Promise.resolve(this.jsDataCcRest.PaymentInstrument.find(piid));

  createOfflinePaymentMethod = (address: PaymentAddress) =>
    Promise.resolve(this.jsDataCcRest.PaymentInstrument
    .create({
      PI: {
        Type: Constants.PaymentInstrumentType.OfflinePaymentMethod,
        Address: address,
      },
    }));
}

export const usePaymentInstruments = (serverContext: CCDataServerContext) => {
  const { cache, tryUpdate } = useCache<PaymentInstrument[]>('paymentInstruments');
  const piActions = useAction(PaymentInstrumentActions, serverContext);

  useEffect(() => {
    if (_.isUndefined(cache) && piActions) {
      tryUpdate(() => piActions.fetchAll());
    }
  }, [piActions, cache, tryUpdate]);

  const createOfflinePaymentMethod = useCallback(
    (address: PaymentAddress) => piActions.createOfflinePaymentMethod(address)
      .then((pi) => {
        tryUpdate(() => Promise.resolve(_.union(cache, [pi])));
        return pi;
      }),
    [cache, piActions, tryUpdate]
  );

  const refreshPiList = useCallback(
    () => {
      tryUpdate(() => piActions.fetchAll(true));
    },
    [piActions, tryUpdate]
  );

  const fetchCtpPiid = useCallback(
    (piid: EntityId) => {
      const piFromCache = _.findWhere(cache, { Id: piid });

      if (piFromCache) {
        return Promise.resolve(piFromCache.CTPPIId);
      }

      return piActions.fetch(piid)
        .tap((pi) => {
          // TODO: this seems to be the source of failure. see if fixed
          tryUpdate(() => Promise.resolve(_.union(cache, [pi])));
        })
        .then(pi => pi.CTPPIId);
    },
    [cache, piActions, tryUpdate]
  );

  return {
    paymentInstruments: cache,
    createOfflinePaymentMethod,
    refreshPiList,
    fetchCtpPiid,
  };
};
